import { motion } from 'framer-motion';
import { Reveal, Eyebrow } from '../Reveal';

export const JOURNEY_STEPS = [
  ['THE QUESTION', 'The beginning of wonder.'],
  ['POSSIBILITY', 'The infinite potential for what could be.'],
  ['CHOICE', 'The moment possibility begins to take form.'],
  ['MOVEMENT', 'The first transition from stillness into becoming.'],
  ['VIBRATION', 'The first rhythm of existence.'],
  ['ENERGY', 'The expression of creation in motion.'],
  ['LIGHT', 'The universe reveals itself.'],
  ['MATTER', 'Structure emerges.'],
  ['STARS', 'The architecture of creation.'],
  ['SOULS', 'Consciousness experiences itself through creation.'],
  ['SOURCE', 'The gathering of all experience, wisdom, memory and consciousness into the whole.'],
  ['THE NEXT QUESTION', 'For every answer becomes the birthplace of another question, and every question opens another possibility.'],
];

const HUES = ['#F5C24B', '#F5C24B', '#F5C24B', '#F5C24B', '#E879F9', '#A78BFA', '#38BDF8', '#2DD4BF', '#34D399', '#F5C24B', '#FBBF24', '#C084FC'];

export default function EternalJourney() {
  return (
    <section id="journey" className="relative py-20 lg:py-32 overflow-hidden" data-testid="eternal-journey-section">
      <div className="absolute inset-0 pointer-events-none" style={{ background: 'radial-gradient(ellipse 60% 50% at 50% 50%, rgba(245,158,11,0.08), transparent 70%)' }} />
      <div className="relative max-w-7xl mx-auto px-5 lg:px-10">
        <Reveal>
          <Eyebrow>The visual map of the site</Eyebrow>
          <h2 className="font-celestial text-3xl sm:text-4xl lg:text-5xl font-light text-amber-100 tracking-wide leading-tight" data-testid="journey-title">THE ETERNAL JOURNEY OF BECOMING</h2>
          <p className="mt-3 font-mono-precision text-[11px] sm:text-xs uppercase tracking-[0.35em] text-amber-300/80">What is, what was and always will be</p>
        </Reveal>

        <div className="mt-12 grid lg:grid-cols-12 gap-10 items-start">
          <motion.figure initial={{ opacity: 0, scale: 0.96 }} whileInView={{ opacity: 1, scale: 1 }} viewport={{ once: true, margin: '-80px' }} transition={{ duration: 1.4, ease: [0.22, 1, 0.36, 1] }} className="lg:col-span-7 relative rounded-3xl overflow-hidden border border-amber-400/20 shadow-[0_60px_120px_-40px_rgba(245,158,11,0.35)]" data-testid="journey-figure">
            <img src="/u99/eternal-journey-of-becoming.png" alt="The Eternal Journey of Becoming — infographic by Kyle Hinton" className="w-full h-auto block" loading="lazy" />
            <figcaption className="absolute bottom-0 inset-x-0 p-4 bg-gradient-to-t from-[#02040a] to-transparent font-mono-precision text-[9px] uppercase tracking-[0.25em] text-slate-400">Centerpiece infographic · Kyle Hinton</figcaption>
          </motion.figure>

          <ol className="lg:col-span-5 grid sm:grid-cols-2 lg:grid-cols-1 gap-x-6" data-testid="journey-steps">
            {JOURNEY_STEPS.map(([name, caption], i) => (
              <Reveal key={name} delay={Math.min(i, 8) * 0.05}>
                <li className="group flex gap-4 py-3.5 border-b border-white/[0.05]" data-testid={`journey-step-${i + 1}`}>
                  <span className="font-celestial text-lg leading-none w-7 shrink-0 pt-0.5" style={{ color: HUES[i] }}>{i + 1}.</span>
                  <div>
                    <p className="font-celestial text-sm sm:text-base tracking-[0.12em]" style={{ color: HUES[i] }}>{name}</p>
                    <p className="mt-1 text-sm text-slate-400 leading-relaxed">{caption}</p>
                  </div>
                </li>
              </Reveal>
            ))}
          </ol>
        </div>

        <Reveal className="mt-16 text-center">
          <p className="font-mono-precision text-[10px] sm:text-[11px] uppercase tracking-[0.3em] text-amber-300/80 leading-loose" data-testid="journey-closing">
            The journey has no true beginning.<br />The journey has no true end.<br />It is the eternal unfolding of existence.
          </p>
          <p className="mt-8 font-celestial text-2xl sm:text-3xl lg:text-4xl text-amber-100 tracking-[0.08em] leading-snug">WHAT IS.<br />WHAT WAS.<br />AND ALWAYS WILL BE.</p>
        </Reveal>
      </div>
    </section>
  );
}
